// src/components/Background.jsx
import React, { useEffect, useRef } from 'react';

const Background = () => {
    const canvasRef = useRef(null);
    const nodes = useRef([]);
    const connections = useRef([]);
    const mousePos = useRef({ x: 0, y: 0 });
    const texts = useRef([
        "Machine Learning", "Data Science", "Neural Networks", "Python",
        "TensorFlow", "PyTorch", "Computer Vision", "NLP",
        "AI Research", "Deep Learning", "Algorithms", "Big Data",
        "Predictive Analytics", "Model Training", "Reinforcement Learning",
        "Generative AI", "Cybernetics", "Cognitive Computing"
    ]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Configuration constants for easier tuning
        const NODE_COUNT = texts.current.length;
        const NODE_MIN_RADIUS = 3;
        const NODE_MAX_RADIUS = 6;
        const NODE_VELOCITY_SCALE = 0.4;
        const CONNECTION_MAX_DISTANCE_FACTOR = 0.25; // Percentage of min(width, height)
        const HOVER_RADIUS = 200;
        const MOUSE_ATTRACTION_FORCE = 0.001;
        const TEXT_FADE_SPEED = 0.95;
        const PULSE_SPEED = 0.02;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initializeNodes();
        };

        const initializeNodes = () => {
            nodes.current = [];
            const cols = Math.ceil(Math.sqrt(NODE_COUNT));
            const rows = Math.ceil(NODE_COUNT / cols);
            const cellWidth = canvas.width / cols;
            const cellHeight = canvas.height / rows;

            for (let i = 0; i < NODE_COUNT; i++) {
                const col = i % cols;
                const row = Math.floor(i / cols);

                nodes.current.push({
                    x: (col * cellWidth + cellWidth / 2) + (Math.random() - 0.5) * cellWidth * 0.7,
                    y: (row * cellHeight + cellHeight / 2) + (Math.random() - 0.5) * cellHeight * 0.7,
                    radius: NODE_MIN_RADIUS + Math.random() * (NODE_MAX_RADIUS - NODE_MIN_RADIUS),
                    baseRadius: NODE_MIN_RADIUS + Math.random() * (NODE_MAX_RADIUS - NODE_MIN_RADIUS),
                    vx: (Math.random() * 2 - 1) * NODE_VELOCITY_SCALE,
                    vy: (Math.random() * 2 - 1) * NODE_VELOCITY_SCALE,
                    color: `hsl(${200 + Math.random() * 40}, 80%, ${60 + Math.random() * 20}%)`, // More vibrant blue/purple HSL
                    text: texts.current[i],
                    textOpacity: 0,
                    pulse: Math.random() * Math.PI * 2,
                    originalVx: (Math.random() * 2 - 1) * NODE_VELOCITY_SCALE,
                    originalVy: (Math.random() * 2 - 1) * NODE_VELOCITY_SCALE,
                });
            }
        };

        const createConnections = () => {
            connections.current = [];
            const maxDistance = Math.min(canvas.width, canvas.height) * CONNECTION_MAX_DISTANCE_FACTOR;

            for (let i = 0; i < nodes.current.length; i++) {
                for (let j = i + 1; j < nodes.current.length; j++) {
                    const dx = nodes.current[i].x - nodes.current[j].x;
                    const dy = nodes.current[i].y - nodes.current[j].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);

                    if (distance < maxDistance) {
                        connections.current.push({
                            node1: i,
                            node2: j,
                            opacity: 0.2 + (1 - distance / maxDistance) * 0.6, // Faint but visible
                            width: 0.5 + (1 - distance / maxDistance) * 1.5
                        });
                    }
                }
            }
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Update nodes
            nodes.current.forEach(node => {
                node.x += node.vx;
                node.y += node.vy;
                node.pulse += PULSE_SPEED;
                node.radius = node.baseRadius * (1 + Math.sin(node.pulse) * 0.2); // More pronounced pulse

                // Boundary check with bounce
                if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
                if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

                // Text fade effect
                node.textOpacity *= TEXT_FADE_SPEED;
                node.textOpacity = Math.max(0, node.textOpacity - 0.005); // Gradual fade out
            });

            createConnections();

            // Draw connections with improved gradient and glow
            connections.current.forEach(conn => {
                const node1 = nodes.current[conn.node1];
                const node2 = nodes.current[conn.node2];

                const gradient = ctx.createLinearGradient(
                    node1.x, node1.y,
                    node2.x, node2.y
                );
                gradient.addColorStop(0, `hsla(200, 80%, 70%, ${conn.opacity * 0.7})`);
                gradient.addColorStop(1, `hsla(240, 70%, 50%, ${conn.opacity * 0.7})`);

                ctx.beginPath();
                ctx.moveTo(node1.x, node1.y);
                ctx.lineTo(node2.x, node2.y);
                ctx.strokeStyle = gradient;
                ctx.lineWidth = conn.width;
                ctx.shadowBlur = 5; // Add subtle glow to connections
                ctx.shadowColor = `hsla(220, 80%, 70%, ${conn.opacity * 0.5})`;
                ctx.stroke();
                ctx.shadowBlur = 0; // Reset shadow
            });

            // Draw nodes and text
            nodes.current.forEach(node => {
                // Node glow effect
                ctx.beginPath();
                ctx.arc(node.x, node.y, node.radius * 2, 0, Math.PI * 2);
                ctx.fillStyle = `hsla(220, 80%, 70%, ${0.1 + node.textOpacity * 0.1})`; // Stronger glow on hover
                ctx.fill();

                // Draw main node
                ctx.beginPath();
                ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
                ctx.fillStyle = node.color;
                ctx.fill();

                // Draw text if visible
                if (node.textOpacity > 0.05) {
                    ctx.font = 'bold 16px "Inter", Arial, sans-serif'; // Bolder and slightly larger text
                    ctx.fillStyle = `rgba(255, 255, 255, ${node.textOpacity})`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(node.text, node.x, node.y - node.radius - 15);
                }
            });

            requestAnimationFrame(animate);
        };

        const handleMouseMove = (e) => {
            mousePos.current = { x: e.clientX, y: e.clientY };
            nodes.current.forEach(node => {
                const dx = mousePos.current.x - node.x;
                const dy = mousePos.current.y - node.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < HOVER_RADIUS) {
                    // Attract nodes to mouse
                    const force = (1 - distance / HOVER_RADIUS) * MOUSE_ATTRACTION_FORCE;
                    node.vx += dx * force;
                    node.vy += dy * force;

                    // Push nodes away for a "repel" effect if too close
                    if (distance < node.radius + 10) {
                        node.vx -= dx * 0.05;
                        node.vy -= dy * 0.05;
                    }

                    // Show text more strongly
                    node.textOpacity = Math.min(1, Math.max(node.textOpacity, 1 - distance / HOVER_RADIUS));
                } else {
                    // Gradually return to original velocity
                    node.vx += (node.originalVx - node.vx) * 0.02;
                    node.vy += (node.originalVy - node.vy) * 0.02;
                }
            });
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        window.addEventListener('mousemove', handleMouseMove);
        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed top-0 left-0 w-full h-full -z-10"
            style={{
                background: '#0a0a0e',
                opacity: 1,
                willChange: 'transform' // Add this line
            }}
        />
    );
};

export default Background;